import { forwardRef, chakra } from '@chakra-ui/system';
import type { SystemStyleObject } from '@chakra-ui/system';

import { type IconProps } from '@frontend/legacy-components';
import { FileProcessingState } from '@frontend/services';

export type SpinnerWithCubeProps = Partial<IconProps> & {
  className?: string;
  label?: string;
  processingState?: FileProcessingState;
  processingColor?: string;
  speed?: string;
  queueColor?: string;
};

/**
 * @deprecated use PartFileCircularProgress instead.
 */
export const SpinnerWithCube = forwardRef<SpinnerWithCubeProps, 'svg'>(
  (
    {
      queueColor = '#767676',
      processingColor = '#FF771A',
      processingState,
      size = '1em',
      speed = '0.45s',
      label = !processingState || processingState === FileProcessingState.QUEUED
        ? 'queued...'
        : processingState === FileProcessingState.PROCESSING
        ? 'processing...'
        : 'processed',
      className,
      __css,
      ...restProps
    },
    ref,
  ) => {
    const svgStyles: SystemStyleObject = {
      w: size,
      h: size,
      lineHeight: size,
      display: 'inline-block',
      flexShrink: 0,
      ...__css,
    };

    //Note: Image of cube with spinner was created with inkscape and extracted
    //* The first path is the spinner. It is only visible if 'processing'. And it is animated using animateTransform.
    //* The second path is the <PaperlessCubeIcon> (see `representative.js`). I combined the 3 paths into one so that the fill only has to be applied once
    //* The spinner stokeWidth is 0.7, and the radius is 8 (diameter 16).  For the boundingBox to fit, half the strokeWidth is added to each side, which makes viewBox 16.7 x 16.7.
    return (
      <chakra.div ref={ref} className={className}>
        <chakra.svg
          verticalAlign="middle"
          viewBox="0 0 16.7 16.7"
          __css={svgStyles}
          {...restProps}
        >
          {processingState === FileProcessingState.PROCESSING && (
            <path
              d="m 0.35,8.35 c 0,-4.35 3.5,-8 8,-8 4.35,0 8,3.5 8,8"
              stroke={processingColor}
              strokeWidth="0.7"
              fill="none"
            >
              <animateTransform
                attributeName="transform"
                attributeType="XML"
                type="rotate"
                from="0 8.35 8.35"
                to="360 8.35 8.35"
                dur={speed}
                repeatCount="indefinite"
              />
            </path>
          )}
          {processingState !== FileProcessingState.PROCESSED && (
            <path
              d="M 7.6953125 2.7910156 L 3.4667969 5.6347656 L 9.0039062 7.703125 L 13.232422 4.859375 L 7.6953125 2.7910156 z M 13.599609 5.3613281 L 9.3535156 8.2167969 L 9.3535156 13.775391 L 13.599609 10.919922 L 13.599609 5.3613281 z M 3.0996094 6.1621094 L 3.0996094 11.802734 L 8.734375 13.908203 L 8.734375 8.265625 L 3.0996094 6.1621094 z"
              fill={
                processingState === FileProcessingState.PROCESSING
                  ? processingColor
                  : queueColor
              }
            />
          )}
        </chakra.svg>
        {label && <chakra.span srOnly>{label}</chakra.span>}
      </chakra.div>
    );
  },
);
SpinnerWithCube.displayName = 'SpinnerWithCube';
